import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/admin-layout';
import {
  Card,
  Table,
  Th,
  Td,
  Button,
  Input,
  Select,
  Pagination,
  Alert,
  Badge,
} from '../components/ui';
import { getSlipHonorariumList } from '../features/honorarium/api';
import { getSekolahList } from '../features/master/api';
import { getPeriodeHonorList } from '../features/honorarium/api';
import type { SlipHonorarium, PeriodeHonor, Sekolah } from '@app/shared';

const rupiah = new Intl.NumberFormat('id-ID', {
  style: 'currency',
  currency: 'IDR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const BULAN = [
  'Januari',
  'Februari',
  'Maret',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Agustus',
  'September',
  'Oktober',
  'November',
  'Desember',
];

export function SlipHonorariumPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<SlipHonorarium[]>([]);
  const [meta, setMeta] = useState({ page: 1, limit: 10, total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [periodeList, setPeriodeList] = useState<PeriodeHonor[]>([]);
  const [sekolahList, setSekolahList] = useState<Sekolah[]>([]);

  const [periodeId, setPeriodeId] = useState<number | ''>('');
  const [sekolahId, setSekolahId] = useState<number | ''>('');
  const [search, setSearch] = useState('');
  const [statusSlip, setStatusSlip] = useState<'' | 'draft' | 'final'>('');

  useEffect(() => {
    getPeriodeHonorList({ limit: 1000, sort_by: 'tahun', sort_dir: 'desc' })
      .then((res) => setPeriodeList(res.rows))
      .catch(() => setError('Gagal memuat daftar periode'));
    getSekolahList({ limit: 1000 })
      .then((res) => setSekolahList(res.rows))
      .catch(() => setError('Gagal memuat daftar sekolah'));
  }, []);

  const load = async (page = 1) => {
    if (!periodeId) {
      setRows([]);
      setMeta({ page: 1, limit: 10, total: 0, total_pages: 1 });
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await getSlipHonorariumList({
        periode_id: periodeId,
        sekolah_id: sekolahId || undefined,
        search: search || undefined,
        status_slip: statusSlip || undefined,
        page,
        limit: 10,
      });
      setRows(result.rows);
      setMeta(result.meta);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal memuat data slip');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
  }, [periodeId, sekolahId, statusSlip]);

  useEffect(() => {
    const t = setTimeout(() => load(1), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fmtPeriode = (p: PeriodeHonor) =>
    `${p.kode_periode} – ${p.nama} (${BULAN[p.bulan - 1]} ${p.tahun})`;

  return (
    <AdminLayout title="Slip Honorarium">
      {error && <Alert type="error">{error}</Alert>}
      <Card>
        <div
          style={{
            display: 'flex',
            gap: '1rem',
            marginBottom: '1rem',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
          }}
        >
          <Button variant="secondary" onClick={() => navigate('/honorarium')}>
            Kelola Periode
          </Button>
          <Select
            label="Periode"
            value={String(periodeId)}
            onChange={(e) => setPeriodeId(e.target.value ? Number(e.target.value) : '')}
            style={{ flex: 2, minWidth: '240px' }}
          >
            <option value="">Pilih periode honor</option>
            {periodeList.map((p) => (
              <option key={p.id} value={p.id}>
                {fmtPeriode(p)}
              </option>
            ))}
          </Select>
          <Select
            label="Sekolah"
            value={String(sekolahId)}
            onChange={(e) => setSekolahId(e.target.value ? Number(e.target.value) : '')}
            style={{ flex: 2, minWidth: '200px' }}
          >
            <option value="">Semua Sekolah</option>
            {sekolahList.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nama}
              </option>
            ))}
          </Select>
          <Select
            label="Status Slip"
            value={statusSlip}
            onChange={(e) => setStatusSlip(e.target.value as '' | 'draft' | 'final')}
            style={{ flex: 1, minWidth: '140px' }}
          >
            <option value="">Semua Status</option>
            <option value="draft">Draft</option>
            <option value="final">Final</option>
          </Select>
          <Input
            label="Cari Pegawai"
            placeholder="Nama pegawai..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 2, minWidth: '200px' }}
          />
        </div>

        <Table>
          <thead>
            <tr>
              <Th>Nama</Th>
              <Th>NIP/NIK</Th>
              <Th>Sekolah</Th>
              <Th>Jabatan</Th>
              <Th>Periode</Th>
              <Th>Status</Th>
              <Th style={{ textAlign: 'right' }}>Total Honor</Th>
              <Th style={{ textAlign: 'center' }}>Aksi</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <Td>{row.pegawai_nama}</Td>
                <Td>{row.pegawai_nip_nik || '-'}</Td>
                <Td>{row.sekolah_nama || '-'}</Td>
                <Td>{row.jabatan_nama || '-'}</Td>
                <Td>{row.kode_periode}</Td>
                <Td>
                  <Badge color={row.status_slip === 'final' ? 'red' : 'gray'}>
                    {row.status_slip === 'final' ? 'Final' : 'Draft'}
                  </Badge>
                </Td>
                <Td style={{ textAlign: 'right' }}>{rupiah.format(row.total_honor)}</Td>
                <Td style={{ textAlign: 'center' }}>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      navigate(
                        `/honorarium/slip/${row.pegawai_id}?periode_id=${row.periode_honor_id}`,
                      )
                    }
                  >
                    Lihat Slip
                  </Button>
                </Td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <Td colSpan={8} style={{ textAlign: 'center' }}>
                  {periodeId ? 'Tidak ada data slip' : 'Pilih periode untuk menampilkan slip'}
                </Td>
              </tr>
            )}
          </tbody>
        </Table>
        {loading && <p style={{ textAlign: 'center', marginTop: '1rem' }}>Memuat...</p>}
        <Pagination page={meta.page} totalPages={meta.total_pages} onChange={load} />
      </Card>
    </AdminLayout>
  );
}
